/**
 * Панель кошелька: кристаллы улья и кассы, долг, ступень долга, остаток суточного капа дани.
 *
 * Назначение файла: показать игроку деньги так, как требует ТЗ — два субсчёта раздельно,
 * долг отдельной строкой, купленные кристаллы визуально неприкосновенны
 * (docs/economy/01-currencies.md).
 *
 * Импорты: форматтеры из @hive/i18n, представление кошелька из игрового слоя.
 *
 * Текста в компоненте нет: все надписи приходят из каталога (правило 11 из AGENTS.md).
 */

import { formatCrystals, formatCrystalsSigned, type LocaleCode } from "@hive/i18n";
import { useTranslation } from "react-i18next";

import type { WalletView } from "../game/economy.js";

import { Crystals } from "./CrystalIcon.jsx";

export interface WalletBarProps {
  readonly locale: LocaleCode;
  readonly wallet: WalletView;
}

export function WalletBar({ locale, wallet }: WalletBarProps): React.JSX.Element {
  const { t } = useTranslation("economy");

  return (
    <section className="wallet" aria-label={t("wallet.title")}>
      <div className="wallet__item">
        <span className="wallet__label">{t("wallet.hive")}</span>
        <span className="wallet__value wallet__value--hive">
          <Crystals>{formatCrystals(locale, wallet.hive, "hive")}</Crystals>
        </span>
      </div>

      <div className="wallet__item">
        <span className="wallet__label">{t("wallet.cash")}</span>
        <span className="wallet__value wallet__value--cash">
          <Crystals>{formatCrystals(locale, wallet.cash, "cash")}</Crystals>
        </span>
      </div>

      {wallet.held > 0 && (
        <p className="wallet__note">
          <Crystals>{t("wallet.held", { amount: formatCrystals(locale, wallet.held, "hive") })}</Crystals>
        </p>
      )}

      <div className="wallet__item">
        <span className="wallet__label">{t("debt.title")}</span>
        {wallet.debt > 0 ? (
          <span className="wallet__value wallet__value--debt">
            <Crystals>{formatCrystalsSigned(locale, -wallet.debt, "hive")}</Crystals>
          </span>
        ) : (
          <span className="wallet__value wallet__value--calm">{t("wallet.debt_none")}</span>
        )}
      </div>

      {wallet.debt > 0 && (
        <p className="wallet__tier">{t(`debt.tier.${wallet.tier}`)}</p>
      )}
    </section>
  );
}
